import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

type RoadmapItem = {
  label: string;
  desc: string;
  color: string;
  status: "now" | "next" | "future";
};

const ITEMS: RoadmapItem[] = [
  {
    label: "Infotainment",
    desc: "AAOS emulators, cloud workstations, full software factory",
    color: "#4ade80",
    status: "now",
  },
  {
    label: "ADAS Developers",
    desc: "Sensor simulation, perception pipeline testing, scenario replay",
    color: "#22d3ee",
    status: "next",
  },
  {
    label: "Cloud Developers",
    desc: "Vehicle-to-cloud services, OTA update pipelines, fleet analytics",
    color: "#60a5fa",
    status: "next",
  },
  {
    label: "Embedded & Controls",
    desc: "AUTOSAR Classic/Adaptive, RTOS integration, model-in-the-loop",
    color: "#818cf8",
    status: "future",
  },
  {
    label: "HIL & Benches",
    desc: "Hardware-in-the-loop orchestration, test bench automation, CI/CD integration",
    color: "#a78bfa",
    status: "future",
  },
  {
    label: "Developer Workstations",
    desc: "Managed on-premise hardware with cloud-synced environments and remote access",
    color: "#c084fc",
    status: "future",
  },
];

const SVG_W = 900;
const ITEM_H = 56;
const GAP = 10;
const START_Y = 20;
const TIMELINE_X = 110;
const CARD_X = 140;
const CARD_W = 700;

const STATUS_LABELS: Record<string, { label: string; color: string; x: number }> = {
  now: { label: "NOW", color: "#4ade80", x: 20 },
  next: { label: "NEXT", color: "#22d3ee", x: 16 },
  future: { label: "FUTURE", color: "#818cf8", x: 10 },
};

export function BeyondInfotainmentSlide() {
  const svgH = START_Y + ITEMS.length * (ITEM_H + GAP) + 20;

  return (
    <Box w="100%" maw={1400} px="xl" h="100%">
      <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
        <SlideTitle
          title="Beyond Infotainment"
          subtitle="Platform roadmap — expanding developer experience across all vehicle domains"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          style={{ flex: 1, minHeight: 0 }}
        >
          <svg
            viewBox={`0 0 ${SVG_W} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "66vh" }}
          >
            {/* Timeline line */}
            <line
              x1={TIMELINE_X}
              y1={START_Y}
              x2={TIMELINE_X}
              y2={START_Y + ITEMS.length * (ITEM_H + GAP) - GAP}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={2}
            />

            {ITEMS.map((item, i) => {
              const y = START_Y + i * (ITEM_H + GAP);
              const sl = STATUS_LABELS[item.status]!;
              const isNow = item.status === "now";

              return (
                <motion.g
                  key={item.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.1, duration: 0.35 }}
                >
                  {/* Status label */}
                  {(i === 0 ||
                    ITEMS[i - 1]!.status !== item.status) && (
                    <text
                      x={sl.x}
                      y={y + ITEM_H / 2 + 4}
                      fill={sl.color}
                      fontSize={9}
                      fontWeight={700}
                      fontFamily="inherit"
                      letterSpacing="0.08em"
                      opacity={0.7}
                    >
                      {sl.label}
                    </text>
                  )}

                  {/* Timeline dot */}
                  <circle
                    cx={TIMELINE_X}
                    cy={y + ITEM_H / 2}
                    r={isNow ? 8 : 5}
                    fill={isNow ? item.color : "none"}
                    stroke={item.color}
                    strokeWidth={2}
                    opacity={isNow ? 1 : 0.6}
                  />
                  {isNow && (
                    <circle
                      cx={TIMELINE_X}
                      cy={y + ITEM_H / 2}
                      r={14}
                      fill={item.color}
                      opacity={0.1}
                    />
                  )}

                  {/* Card */}
                  <rect
                    x={CARD_X}
                    y={y}
                    width={CARD_W}
                    height={ITEM_H}
                    rx={8}
                    fill={
                      isNow
                        ? "rgba(74,222,128,0.06)"
                        : "rgba(255,255,255,0.02)"
                    }
                    stroke={
                      isNow
                        ? "rgba(74,222,128,0.2)"
                        : "rgba(255,255,255,0.06)"
                    }
                    strokeWidth={1}
                  />
                  <text
                    x={CARD_X + 16}
                    y={y + 22}
                    fill={item.color}
                    fontSize={13}
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {item.label}
                  </text>
                  <text
                    x={CARD_X + 16}
                    y={y + 42}
                    fill="rgba(255,255,255,0.5)"
                    fontSize={10}
                    fontFamily="inherit"
                  >
                    {item.desc}
                  </text>
                </motion.g>
              );
            })}
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
